import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
    Autocomplete,
    AutocompleteChangeReason,
    TextField,
    createFilterOptions,
} from '@mui/material';
import { union } from '@npi/buddy-utils';

import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { isEmailValid } from 'src/utils/validations';

export type EmailRecipientsInputProps = {
    setSendRecipients: React.Dispatch<React.SetStateAction<string[]>>;
    sendRecipients: string[];
};

const filter = createFilterOptions<string>();

export const EmailRecipientsInput: React.FC<EmailRecipientsInputProps> = ({
    sendRecipients,
    setSendRecipients,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [inputFocused, setInputFocused] = useState<boolean>();

    const [currentlyTypingEmail, setCurrentlyTypingEmail] = useState<string>();

    const handleChangeRecipients = useCallback(
        (event: any, value: any, reason: AutocompleteChangeReason) => {
            event?.preventDefault();
            event?.stopPropagation();

            if (reason === 'removeOption') {
                if (event.code === 'Backspace') {
                    if (value.length === 1) {
                        // Value is the new list without the option removed
                        setSendRecipients([]);
                        showSuccessToast(`${value[0]} removed from recipients`);
                        return;
                    }
                    // remove last element in array
                    const emailToRemove = value?.[value?.length - 1];
                    const newValue = value.slice(0, value.length - 1);
                    setSendRecipients(newValue);
                    if (emailToRemove) {
                        showSuccessToast(`${emailToRemove} removed from recipients`);
                    }

                    return;
                }

                setSendRecipients(value);
                return;
            }

            if (reason === 'clear') {
                setSendRecipients([]);
                setCurrentlyTypingEmail('');
                showSuccessToast('Email recipients cleared');
                return;
            }

            // When pressing enter, the value is an array but for tab and onBlur we pass the value as string from input
            if (Array.isArray(value)) {
                const emailToCheck = value?.[value.length - 1];
                if (!isEmailValid(emailToCheck)) {
                    showErrorToast(`Email entered (${emailToCheck}) is not valid`);
                    setCurrentlyTypingEmail('');
                    return;
                }

                if (sendRecipients?.includes(emailToCheck)) {
                    showErrorToast(`Email ${value} is already present`);
                    setCurrentlyTypingEmail('');
                    return;
                }
            }

            if (sendRecipients?.includes(value)) {
                showErrorToast(`Email ${value} is already present`);
                return;
            } else if (value && !isEmailValid(value)) {
                showErrorToast(`Email entered (${value}) is not valid`);
                return;
            }

            if (value && isEmailValid(value)) {
                const recipientsArray = Array.isArray(value) ? value : [value];
                setSendRecipients((prevState) => {
                    return union(recipientsArray, prevState);
                });
                showSuccessToast('Email added to recipients');
                setCurrentlyTypingEmail('');
            }
        },
        [sendRecipients, setSendRecipients]
    );

    const handleFocused = () => {
        setInputFocused(true);
    };

    const handleBlur = () => {
        setInputFocused(false);
    };

    const handleKeyPress = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            if (event?.code === 'Space') {
                handleChangeRecipients(event, event?.target?.value, 'createOption');
            }
            if (event?.code === 'Enter') {
                handleChangeRecipients(event, event?.target?.value, 'createOption');
            }
            if (event?.code === 'Backspace') {
                if (currentlyTypingEmail?.length === 0) {
                    handleChangeRecipients(event, sendRecipients, 'removeOption');
                }
            }
        },
        [currentlyTypingEmail, handleChangeRecipients, sendRecipients]
    );

    useEffect(() => {
        inputRef?.current?.addEventListener('focus', handleFocused);
        inputRef?.current?.addEventListener('blur', handleBlur);

        return () => {
            inputRef?.current?.removeEventListener('focus', handleFocused);
            inputRef?.current?.addEventListener('blur', handleBlur);
        };
    }, [handleKeyPress]);

    useEffect(() => {
        if (!inputFocused) {
            setCurrentlyTypingEmail('');
            handleChangeRecipients(null, inputRef?.current?.value, 'blur');
        }
    }, [handleChangeRecipients, inputFocused]);

    useEffect(() => {
        if (inputRef && inputRef.current && inputRef.current.value) {
            inputRef.current.value = currentlyTypingEmail;
        }
    }, [currentlyTypingEmail, inputRef?.current?.value]);

    return (
        <Autocomplete
            sx={{
                '&.MuiAutocomplete-root': {
                    '& fieldset': {
                        borderColor: `${'#F39E4E'}`,
                        borderWidth: `${'1px'}`,
                    },
                    '&:hover fieldset': {
                        borderColor: '#F39E4E',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: '#F39E4E',
                    },
                },
            }}
            open={false}
            value={sendRecipients}
            multiple
            options={sendRecipients}
            freeSolo
            id={'recipients'}
            onChange={handleChangeRecipients}
            filterOptions={(options, params) => {
                const filtered = filter(options, params);

                return filtered;
            }}
            ListboxProps={{
                style: { maxHeight: '300px', color: '#000' },
            }}
            getOptionLabel={(option) => {
                return option;
            }}
            renderOption={(props, option) => {
                return <li {...props}>{option}</li>;
            }}
            renderInput={(params) => {
                return (
                    <TextField
                        sx={{
                            '& label': {
                                zIndex: 10,
                            },
                            '&': {
                                flexGrow: 1,
                            },
                        }}
                        inputRef={inputRef}
                        {...params}
                        inputProps={{
                            ...params.inputProps,
                            onKeyDown: handleKeyPress,
                            value: currentlyTypingEmail,
                        }}
                        onChange={(e) => {
                            return setCurrentlyTypingEmail(e.target.value);
                        }}
                        label="Recipient(s)"
                    />
                );
            }}
        />
    );
};
